import {
    CustomUserFeature,
    decodeUserFeatureId,
    FeatureAndUiState,
    FeatureKind,
    PredefinedUserFeature,
    UserFeature
} from "./models";
import {ISavedFeatureService} from "./saved_features";
import {_F} from "../../utility/func";
import {PinAndExtras} from "../PinApi";
import {FeatureCollection} from "geojson";
import {SavedFeatureServiceEvents} from "./events";
import {FeaturesEvents} from "../events/FeaturesEvents";
import {ILoginService} from "../oe/login-service";
import {ReportServiceEvents} from "./report_service_events";
import {IReportService} from "./report_service";
import {GeoJSON} from "ol/format";
import {ProjectionLike} from "ol/proj";
import VectorSource from "ol/source/Vector";
import {Geometry, Point} from "ol/geom";
import {all} from "ol/loadingstrategy";
import {Feature} from "ol";
import {GeoAppEvents} from "../../controllers/events/geoapp_events";
import {IToastResult, IToastService, TOAST_DURATION_LONG} from "../ToastService";
import arrayHead = _F.arrayHead;
import getSrid = _F.getSrid;
import isMapPage = _F.isMapPage;
import {PinReport, ReportKind} from "../ISharedFeatureState";


function featureId(userFeatureType: FeatureKind, userFeatureId: string|number, featureIdx: number, saved: boolean = true) : string {
    return `${!saved ? 'unsaved-' : ''}${userFeatureType}-${userFeatureId}:${featureIdx}`;
}

export class RestfulSavedFeatureService implements ISavedFeatureService {
    public static $inject = ['$rootScope','$q', '$timeout', '$http', 'loginService', 'reportService', 'toastService'];

    protected fmt = new GeoJSON();
    protected dataProjection : ProjectionLike = 'EPSG:2193';

    protected _featuresSourceReadyDef: ng.IDeferred<VectorSource<Geometry>> = this.$q.defer();

    featuresSourceReady(): ng.IPromise<VectorSource<Geometry>> {
        return this._featuresSourceReadyDef.promise;
    }

    public featuresSource = new VectorSource({
        url: '/api/GeoFeature',

        loader: (extent, resolution, projection) => {
            let done = () => {
                this._featuresSourceReadyDef.resolve(this.featuresSource);
            };

            this.features().then((fs) => {
                fs.forEach((f) => {
                    const fmtOptions = {
                        dataProjection: f.userFeature.srid ? `EPSG:${f.userFeature.srid}` : this.dataProjection,
                        featureProjection: projection
                    };

                    let geoJSONFeatures = f.userFeature.featureCollection.features;
                    if (!geoJSONFeatures) {
                        console.warn(`No features`);
                        console.warn(f);
                        return;
                    }

                    let features = geoJSONFeatures.map((geojsonFeature) => {
                            return this.fmt.readFeature(geojsonFeature, fmtOptions);
                        });

                    let uf = f.userFeature;
                    features.forEach((feature, i) => {
                        feature.setId(featureId(
                                f.userFeature.kind,
                                f.userFeature.id,
                                i
                            ));
                        feature.set('saved', true);
                        feature.set('sourceLayerId', uf['sourceLayerId'] || undefined);
                        feature.set('hidden', !f.visible);
                        feature.unset('selected');
                    });

                    if (f.userFeature.geometryType == 'Point') {
                        features.forEach((feature) => {
                            let geom = feature.getGeometry();
                            if (!(geom instanceof Point)) return;

                            const pinData = <PinAndExtras> {
                                hidden: !f.visible,
                                checked: false,
                                data: {
                                    title: f.userFeature.title,
                                    x: geom.getCoordinates()[0],
                                    y: geom.getCoordinates()[1]
                                }
                            };
                            feature.set('pin', pinData);

                            const rp: PinReport = {
                                kind: ReportKind.pin,
                                point: feature as Feature<Point>,
                                queryResults: {},
                                userFeatures: f
                            };

                            feature.set('pinReport', rp);
                        });
                    }

                    f.mapFeatures = features;
                    this.featuresSource.addFeatures(features);
                });

                done();
            }, done);
        },
        strategy: all
    });

    public customFeatures = {
        loading: false,
        features: <FeatureAndUiState[]> []
    };

    public predefinedFeatures = {
        loading: false,
        features: []
    };

    constructor(protected $rootScope: ng.IRootScopeService,
                protected $q: ng.IQService,
                protected $timeout: ng.ITimeoutService,
                protected $http: ng.IHttpService,
                protected loginService: ILoginService,
                protected reportService: IReportService,
                protected toastService: IToastService) {
        let init = () => {
            this.customFeatures.loading = true;
            this.predefinedFeatures.loading = true;

            delete this._features;
            this.features().then((fs) => {
                this.customFeatures.features = fs.filter((f) => f.userFeature.kind == 'custom');
                this.predefinedFeatures.features = fs.filter((f) => f.userFeature.kind == 'predefined');
            }, () => {

            }).finally(() => {
                this.predefinedFeatures.loading = false;
                this.customFeatures.loading = false;
            });
        };

        if (isMapPage()) init();

        this.$rootScope.$on(GeoAppEvents.resetMapAndLayers, () => {
            ([
                ...this.customFeatures.features || [],
                ...this.predefinedFeatures.features || []
            ]).forEach(f => {
                f.visible = f.userFeature.geometryType == 'Point';
                (f.mapFeatures || []).forEach(mf => mf.set('hidden', !f.visible));
            });
        });

        this.$rootScope.$on('loginService:success', (ev, currentMember: number, previousMember: number) => {
            if (currentMember == previousMember) {
                return;
            }

            if (currentMember) {
                if (previousMember) {
                    this.featuresSource.clear();
                }

                init();
                let loadedRTree = (<any> this.featuresSource).loadedExtentsRtree_;
                if (loadedRTree && loadedRTree.clear) loadedRTree.clear();
            }

        });

        this.$rootScope.$on(ReportServiceEvents.reportChanged, (ev, currentValue: any, oldValue: any) => {
            if (currentValue && oldValue && currentValue.shared !== oldValue.shared) {
                const subjectId = currentValue && currentValue.subject && currentValue.subject.id;

                this.features().then(fs => {
                    fs.filter(f => f.userFeature.id === subjectId)
                        .forEach(f => {
                            const reports = this.reportService.reportsForUserFeatureId(f.userFeature.id);
                            const shared = reports.some((r: any) => r && r.shared);
                            if (f.userFeature.shared != shared) {
                                f.userFeature.shared = shared;
                                this.saveFeature(f, ['shared']);
                            }
                        });
                }, () => { });
            }
        });

        this.$rootScope.$on(FeaturesEvents.featureRemoved, (ev, feature: Feature<any>) => {
            let fid = feature.getId();
            if (!fid) return;

            let ufId = decodeUserFeatureId(fid.toString());

            if (!ufId) return;

            let fs = ufId.kind == 'custom' ? this.customFeatures.features : this.predefinedFeatures.features;

            fs.filter((uf) => uf.userFeature.id == ufId.id)
                .forEach((uf) => {
                    if (uf.mapFeatures) {
                        uf.mapFeatures = uf.mapFeatures.filter((f) => f != feature);
                        if (!uf.mapFeatures.length) {
                            this.deleteFeature(uf);
                            return;
                        }
                    }

                });
        });
    }

    protected url: string = '/api/GeoFeature';

    protected defaultParams = {
        'Created__sort': 'ASC'
    };

    _features: ng.IPromise<FeatureAndUiState[]> = null;
    features(): ng.IPromise<FeatureAndUiState[]> {
        if (this._features) return this._features;

        if (this.loginService.currentMemberId()) {
            this._features = this.$http.get<UserFeature[]>(this.url, {
                params: this.defaultParams
            }).then((resp) => {
                return resp.data.map((f) => {
                    return {
                        userFeature: f,
                        visible: f.geometryType == 'Point',
                        deleting: false
                    };
                })
            });
        } else {
            this._features = this.$q.reject('not logged in');
        }

        return this._features;
    }

    removeFeatureFromUI(feature: FeatureAndUiState) {
        const uf = feature && feature.userFeature;

        if (uf.kind == 'predefined') {
            this.predefinedFeatures.features = this.predefinedFeatures.features.filter((f) => f!= feature);
        } else {
            this.customFeatures.features = this.customFeatures.features.filter((f) => f!= feature);
        }

        (feature.mapFeatures || [])
            .forEach((f) => this.featuresSource.removeFeature(f));
        delete feature.userFeature.id;
        this.$rootScope.$broadcast(SavedFeatureServiceEvents.featureDeleted, feature);
    }

    currentDeleteOperation: ng.IDeferred<IToastResult>;

    deleteFeature(feature: FeatureAndUiState) : ng.IPromise<void> {
        let id = feature.userFeature.id;

        if (!id) {
            this.removeFeatureFromUI(feature);
        }

        if (this.currentDeleteOperation) {
            this.currentDeleteOperation.resolve({ actionClicked: false });
        }

        feature.deleting = true;
        (feature.mapFeatures || []).forEach(f => f.set('deleting', true))

        const deleteOp = this.$q.defer<IToastResult>();
        this.currentDeleteOperation = deleteOp;

        const toast = this.toastService.showToast(
            `Deleting ${feature.userFeature.title}`,
            TOAST_DURATION_LONG,
            undefined,
            {
                label: 'Cancel',
                onClick() {
                    feature.deleting = false
                }
            }, "SavedFeatureService:deleteFeature");

        return this.$q.race([deleteOp.promise, toast]).then(result => {
            if (!result.actionClicked) {
                return this.$http.delete(this.featureUrl(id)).then(() => this.removeFeatureFromUI(feature));
            } else {
                feature.deleting = false;
                (feature.mapFeatures || []).forEach(f => f.set('deleting', false));
            }
        }, () => {
            feature.deleting = false;
            (feature.mapFeatures || []).forEach(f => f.set('deleting', false));
        });
    }

    validateFeature(f: object) {
        if ('title' in f
            && !f['title'] || !`${f['title']}`.trim().length) {
            throw new Error('title is mandatory');
        }
    }

    private featureUrl(id: number|string): string {
        return `${this.url}/${id}`;
    }

    loadFeature(id: number|string): ng.IHttpPromise<UserFeature> {
        return this.$http.get<UserFeature>(this.featureUrl(id));
    }

    saveFeature(f: FeatureAndUiState, columns: string[] = null): ng.IPromise<void> {
        f.saving = true;

        let addToList = () => {
            if (f.userFeature.kind == 'predefined') {
                this.predefinedFeatures.features.push(f);
            } else {
                this.customFeatures.features.push(f);
            }
        };

        if (f.userFeature.id) {
            let putData : object = f.userFeature;

            if (columns && columns.length) {
                let restrictedPutData = {};

                columns.forEach((column) => {
                        restrictedPutData[column] = putData[column];
                    });

                putData = restrictedPutData;
            }

            try {
                this.validateFeature(putData);
            } catch (e) {
                f.saving = false;
                return this.$q.reject(e.message);
            }

            return this.$http.put(this.featureUrl(f.userFeature.id), putData)
                .then(() => {
                    f.saving = false;
                    (f.mapFeatures || []).forEach(mf => mf.set('saved', true));
                }, () => {
                    f.saving = false;
                })

        } else {
            try {
                this.validateFeature(f.userFeature)
            } catch (e) {
                return this.$q.reject('invalid feature');
            }

            return this.$http.post<UserFeature>(`${this.url}`, f.userFeature)
                .then((resp) => {
                    f.userFeature.id = resp.data.id;
                    f.saving = false;
                    (f.mapFeatures || []).forEach((feature: Feature<any>, i) => {
                        const fid = featureId(f.userFeature.kind, f.userFeature.id, i);
                        feature.setId(fid);
                        feature.set('saved', true);
                    });
                }, () => {
                    f.saving = false;
                })
                .then(addToList);
        }
    }

    createNewFeatureFromGeoJSON(title: string,
                                featureCollection: FeatureCollection,
                                kind: FeatureKind,
                                featureProjection: ProjectionLike,
                                sourceLayerId?: number,
                                dataProjection?: ProjectionLike): ng.IPromise<UserFeature> {
        dataProjection = dataProjection || "EPSG:4326";

        let geometryType = featureCollection.features[0].geometry.type;

        let srid = getSrid(dataProjection);

        if (kind == 'predefined') {
            let predefinedFeature : PredefinedUserFeature = {
                id: undefined,
                kind,
                title,
                geometryType,
                featureCollection,
                sourceLayerId,
                srid,
            };

            return this.$q.resolve(predefinedFeature);
        } else if (kind == 'custom') {
            let customFeature: CustomUserFeature = {
                id: undefined,
                kind,
                title,
                geometryType,
                featureCollection,
                srid
            };

            return this.$q.resolve(customFeature);
        }
    }

    protected nextId(ids: number[]): number {
        if (!ids.length) return 1;

        return Math.abs(_F.maximum(ids)) + 1;
    }

    createNewFeature(title: string,
                   features: Feature<any>[],
                   kind: FeatureKind,
                   featureProjection: ProjectionLike,
                   sourceLayerId?: number) : ng.IPromise<UserFeature> {
        if (!features || !features.length) {
            return this.$q.reject('No feature');
        }

        const fmtOptions = {
            featureProjection: featureProjection,
            dataProjection: this.dataProjection
        };

        let geojsonFeature: GeoJSON.FeatureCollection = <any> this.fmt.writeFeaturesObject(features, fmtOptions);
        let geometryType: GeoJSON.GeoJsonGeometryTypes = geojsonFeature
            .features.map((f) => f.geometry.type)[0];

        const srid = getSrid(this.dataProjection);

        if (kind == 'custom') {
            let featureIds = this.featuresSource.getFeatures()
                .map(f => f.getId())
                .map(id => {
                    try {
                        return decodeUserFeatureId(id + '');
                    } catch (e) {
                        return null;
                    }
                })
                .filter(f => f && f.kind === 'custom')
                .map(f => parseInt(f.id));

            let id = this.nextId(featureIds);

            features.forEach((f, i) => {
                f.setId(featureId(
                    kind,
                    id,
                    i,
                    false
                ));
            });

            return this.$q.resolve(<CustomUserFeature> {
                kind,
                geometryType,
                title,
                featureCollection: <any> geojsonFeature,
                srid
            });
        } else {
            let featureIds = this.featuresSource.getFeatures()
                .map(f => f.getId())
                .map(id => {
                    try {
                        return decodeUserFeatureId(id + '');
                    } catch (e) {
                        return null;
                    }
                })
                .filter(f => f && f.kind === 'predefined')
                .map(f => parseInt(f.id));

            let id = this.nextId(featureIds);

            features.forEach((f, i) => {
                f.setId(featureId(
                    kind,
                    id,
                    i,
                    false
                ));
            });

            return this.$q.resolve(<PredefinedUserFeature> {
                kind,
                geometryType,
                sourceLayerId,
                title,
                featureCollection: <any> geojsonFeature,
                srid
            });
        }
    }

    updateFeatureVisibility(predicate:(f: FeatureAndUiState) => boolean) {
        this.predefinedFeatures.features.forEach(f => f.visible = predicate(f));
        this.customFeatures.features.forEach(f => f.visible = predicate(f));

        const updateMapFeature = (f: FeatureAndUiState) => {
            (f.mapFeatures || []).forEach(feature => {
                feature.set('hidden', !f.visible);
            });

            /*let mapFeature = this.featuresSource.getFeatureById(f.userFeature.id);
            if (mapFeature) {
                mapFeature.set('hidden', !f.visible);
            }*/
        };

        this.predefinedFeatures.features.forEach(updateMapFeature);
        this.customFeatures.features.forEach(updateMapFeature);
    }

    getMapFeatures(userFeature: UserFeature): Feature<any>[] {
        return userFeature.featureCollection.features
            .map((feature, i) => {
                return featureId(userFeature.kind, userFeature.id, i);
            })
            .map((featureId) => {
                try {
                    return this.featuresSource.getFeatureById(featureId);
                } catch (e) {
                    return null;
                }
            })
            .filter((f) => !!f);
    }

    getUserFeatureForPin(newValue: PinAndExtras): FeatureAndUiState {
        let isPinFeature = (f: FeatureAndUiState) => {
            return f.userFeature && f.userFeature.geometryType == 'Point';
        };

        let pinFeatures = this.customFeatures.features.filter(isPinFeature)
            .concat(this.predefinedFeatures.features.filter(isPinFeature));

        while (pinFeatures.length) {
            let pinFeature : FeatureAndUiState = pinFeatures.pop();
            if (!pinFeature || !pinFeature.mapFeatures) continue;
            let pinData = pinFeature.mapFeatures.map((f) => f.get('pin'));

            if (pinData.indexOf(newValue) !== -1) {
                return pinFeature;
            }
        }
        return null;
    }

    getUserFeatureFromMapFeature(feature: Feature<any>): FeatureAndUiState {
        let id = `${feature.getId()}`;

        let decodedId = decodeUserFeatureId(id);

        if (!decodedId) return null;

        let collection = decodedId.kind == 'predefined' ? this.predefinedFeatures.features : this.customFeatures.features;
        return arrayHead(collection.filter((uf : FeatureAndUiState) => {
            return uf && uf.userFeature && uf.userFeature.id == decodedId.id;
        }));
    }

    updateGeoJSON(featureAndUi: FeatureAndUiState, featureProjection: ProjectionLike) {
        featureAndUi.userFeature.featureCollection = <any> this.fmt.writeFeaturesObject(featureAndUi.mapFeatures, {
            featureProjection,
            dataProjection: this.dataProjection
        });
    }
}
